import { Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, inject } from '@angular/core';
import {CURVA_ANIMACAO_ABA, DESLOCAMENTO_MAXIMO_ABA, DURACAO_ENTRADA_ABA, DURACAO_SAIDA_ABA, FRACAO_ENTRADA_ABA, LIMIAR_TROCA_ABA, LIMIAR_VELOCIDADE_ABA, SELETOR_IGNORAR_ARRASTE_ABA} from './arrastar-abas.constantes';

@Directive({
  selector: '[appArrastarAbas]',
  standalone: true,
  exportAs: 'arrastarAbas'
})
export class ArrastarAbasDirective implements OnInit, OnDestroy {
  @Input() podeAnterior = true;
  @Input() podeProxima = true;
  @Output() abaAnterior = new EventEmitter<void>();
  @Output() proximaAba = new EventEmitter<void>();

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private inicio: { x: number; y: number; id: number } | null = null;
  private ultimo = { x: 0, t: 0 };
  private velocidade = 0;
  private horizontal = false;
  private animando = false;
  private engolirClique = false;
  private temporizador?: ReturnType<typeof setTimeout>;
  private remover?: () => void;

  ngOnInit(): void {
    const host = this.elementRef.nativeElement;
    host.style.touchAction = 'pan-y';
    this.zone.runOutsideAngular(() => {
      const baixar = (e: PointerEvent) => this.aoPressionar(e);
      const mover = (e: PointerEvent) => this.aoMover(e);
      const soltar = (e: PointerEvent) => this.aoSoltar(e);
      const cancelar = () => this.voltarAoCentro();
      const clique = (e: Event) => {
        if (!this.engolirClique) return;
        this.engolirClique = false;
        e.stopPropagation();
        e.preventDefault();
      };
      host.addEventListener('pointerdown', baixar, { passive: true });
      window.addEventListener('pointermove', mover, { passive: true });
      window.addEventListener('pointerup', soltar, { passive: true });
      window.addEventListener('pointercancel', cancelar, { passive: true });
      host.addEventListener('click', clique, true);
      this.remover = () => {
        host.removeEventListener('pointerdown', baixar);
        window.removeEventListener('pointermove', mover);
        window.removeEventListener('pointerup', soltar);
        window.removeEventListener('pointercancel', cancelar);
        host.removeEventListener('click', clique, true);
      };
    });
  }

  ngOnDestroy(): void {
    this.remover?.();
    clearTimeout(this.temporizador);
  }

  animarEntrada(sentido: number): void {
    if (!sentido || this.movimentoReduzido()) return;
    const host = this.elementRef.nativeElement;
    host.style.transition = 'none';
    host.style.transform = `translateX(${sentido * host.clientWidth * FRACAO_ENTRADA_ABA}px)`;
    host.style.opacity = '0';
    requestAnimationFrame(() => requestAnimationFrame(() => {
      host.style.transition = `transform ${DURACAO_ENTRADA_ABA}ms ${CURVA_ANIMACAO_ABA}, opacity ${DURACAO_ENTRADA_ABA}ms ${CURVA_ANIMACAO_ABA}`;
      host.style.transform = '';
      host.style.opacity = '';
      this.animando = false;
    }));
  }

  private aoPressionar(e: PointerEvent): void {
    if (this.animando) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const alvo = e.target as HTMLElement;
    if (alvo?.closest?.(SELETOR_IGNORAR_ARRASTE_ABA)) return;
    const painel = alvo?.closest?.('.cdk-overlay-pane');
    if (painel && !painel.contains(this.elementRef.nativeElement)) return;
    this.inicio = { x: e.clientX, y: e.clientY, id: e.pointerId };
    this.ultimo = { x: e.clientX, t: e.timeStamp };
    this.velocidade = 0;
    this.horizontal = false;
    this.engolirClique = false;
  }

  private aoMover(e: PointerEvent): void {
    const inicio = this.inicio;
    if (!inicio || inicio.id !== e.pointerId) return;
    const dx = e.clientX - inicio.x;
    const dy = e.clientY - inicio.y;
    if (!this.horizontal) {
      if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) {
        this.inicio = null;
        return;
      }
      if (Math.abs(dx) < 12) return;
      if (e.pointerType === 'mouse' && window.getSelection()?.toString()) {
        this.inicio = null;
        return;
      }
      this.horizontal = true;
    }
    const intervalo = e.timeStamp - this.ultimo.t;
    if (intervalo > 0) this.velocidade = (e.clientX - this.ultimo.x) / intervalo;
    this.ultimo = { x: e.clientX, t: e.timeStamp };

    const host = this.elementRef.nativeElement;
    const largura = host.clientWidth || 1;
    const visual = this.podeIr(dx) ? dx : Math.sign(dx) * Math.min(DESLOCAMENTO_MAXIMO_ABA, Math.abs(dx) * 0.3);
    host.style.transition = 'none';
    host.style.transform = `translateX(${visual}px)`;
    host.style.opacity = this.podeIr(dx) ? `${1 - Math.min(0.6, Math.abs(dx) / largura)}` : '';
  }

  private aoSoltar(e: PointerEvent): void {
    const inicio = this.inicio;
    if (!inicio || inicio.id !== e.pointerId) return;
    const dx = e.clientX - inicio.x;
    const rapido = Math.abs(this.velocidade) >= LIMIAR_VELOCIDADE_ABA && Math.sign(this.velocidade) === Math.sign(dx);
    const trocou = this.horizontal && this.podeIr(dx) && (Math.abs(dx) >= LIMIAR_TROCA_ABA || rapido);
    this.inicio = null;
    this.horizontal = false;
    if (!trocou) {
      this.voltarAoCentro();
      return;
    }
    this.engolirClique = true;
    setTimeout(() => (this.engolirClique = false), 400);
    this.sairPara(Math.sign(dx));
  }

  private sairPara(sentido: number): void {
    const host = this.elementRef.nativeElement;
    const emitir = () => this.zone.run(() => (sentido < 0 ? this.proximaAba.emit() : this.abaAnterior.emit()));
    if (this.movimentoReduzido()) {
      this.voltarAoCentro();
      emitir();
      return;
    }
    this.animando = true;
    host.style.transition = `transform ${DURACAO_SAIDA_ABA}ms ease-in, opacity ${DURACAO_SAIDA_ABA}ms ease-in`;
    host.style.transform = `translateX(${sentido * host.clientWidth * 0.6}px)`;
    host.style.opacity = '0';
    this.temporizador = setTimeout(() => {
      emitir();
      this.animarEntrada(-sentido);
    }, DURACAO_SAIDA_ABA);
  }

  private voltarAoCentro(): void {
    this.inicio = null;
    this.horizontal = false;
    const host = this.elementRef.nativeElement;
    host.style.transition = `transform 240ms ${CURVA_ANIMACAO_ABA}, opacity 240ms ${CURVA_ANIMACAO_ABA}`;
    host.style.transform = '';
    host.style.opacity = '';
  }

  private podeIr(dx: number): boolean {
    return dx < 0 ? this.podeProxima : this.podeAnterior;
  }

  private movimentoReduzido(): boolean {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }
}
